/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { get } from 'js/lib/user';

import { Avatar, Tag, Typography2, typography2 } from '@coursera/cds-core';
import { track } from '@coursera/event-pulse/sdk';

import ActiveSubscriptions from 'bundles/composable-page-header/components/ActiveSubscriptions';
import LanguageTranslationMenuItem from 'bundles/composable-page-header/components/mobile-menu-items/LanguageTranslationMenuItem';
import MobileButton from 'bundles/composable-page-header/components/mobile-menu-items/MobileButton';
import MobileLogOutButton from 'bundles/composable-page-header/components/mobile-menu-items/MobileLogOutButton';
import type { AccountDropdownLink } from 'bundles/composable-page-header/types';

import _t from 'i18n!nls/composable-page-header';

type Props = {
  dropdownLinks: Array<AccountDropdownLink>;
  hideLanguage?: boolean;
  courseraPlusSubscriptionCycle?: string | null;
  hasCourseraPlus?: boolean;
};

const styles = {
  fullWrapper: css`
    padding-top: var(--cds-spacing-100);
  `,
  fullName: css`
    align-self: center;
    color: var(--cds-color-neutral-primary);
    display: -webkit-box;
    font: ${typography2.subtitleMedium.font};
    letter-spacing: ${typography2.subtitleMedium.letterSpacing};
    line-height: var(--cds-line-height-250);
    margin-left: var(--cds-spacing-100);
    max-height: calc(3 * var(--cds-line-height-200));
    overflow: hidden;
    text-align: left;
    text-overflow: ellipsis;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  `,
  nameAndPicture: css`
    display: flex;
    flex-direction: row;
  `,
  navButton: css`
    width: 100%;
    background-color: transparent;
    border: none;
    text-align: left;
    position: relative;
    padding: var(--cds-spacing-100);
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: var(--cds-color-neutral-primary);
    margin-bottom: var(--cds-spacing-100);
  `,
  avatar: css`
    &.cds-Avatar-base {
      overflow: visible;
    }
  `,
  yourAccount: css`
    padding-top: var(--cds-spacing-200);
    padding-bottom: var(--cds-spacing-50);
  `,
  avatarInExperiment: css`
    &.cds-Avatar-showBorder {
      border: 2px solid var(--cds-color-interactive-primary);
    }
  `,
  arrow: css`
    flex-shrink: 0;
  `,
  plusBadge: css`
    padding: 0 6px;
    position: relative;
    top: -8px;
    height: 10px;

    span {
      font-size: 8px;
    }

    &.cds-tag-status.cds-tag-primary.cds-tag-default {
      background-color: var(--cds-color-interactive-primary);
      border-color: var(--cds-color-interactive-primary);
    }
  `,
  ActiveSubscriptionsBorder: css`
    border-bottom: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-top: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    margin: var(--cds-spacing-50) 0;
  `,
  border: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: -10px;
  `,
};

const AccountMenuItemExpanded = ({
  dropdownLinks,
  hideLanguage,
  hasCourseraPlus,
  courseraPlusSubscriptionCycle,
}: Props) => {
  const { fullName, photo: defaultPhoto, photo_120: photoUrl } = get();

  const linkItemClick = (link: AccountDropdownLink) => {
    track('click_profile_menu_item', {
      profileItemName: link.name ?? '',
      profileItemLink: link.href,
    });
  };

  const mobileMenuItems = dropdownLinks.reduce<React.ReactNode[]>((acc, link) => {
    acc.push(
      <MobileButton
        key={link.name}
        href={link.href}
        label={link.label}
        name={link.name}
        onButtonClick={() => linkItemClick(link)}
      />
    );

    if (link.name === 'account-settings' && !hideLanguage) {
      acc.push(<LanguageTranslationMenuItem key="language" />);
    }

    return acc;
  }, []);

  return (
    <div css={styles.fullWrapper}>
      <div css={styles.nameAndPicture}>
        <div css={hasCourseraPlus && styles.border}>
          <Avatar
            variant="user"
            data-testid="user-avatar"
            initial={fullName?.[0]}
            imgProps={{
              src: photoUrl || defaultPhoto,
              alt: _t('Profile image for #{learnerName}', { learnerName: fullName }),
            }}
            css={[styles.avatar, hasCourseraPlus && styles.avatarInExperiment]}
            showBorder={hasCourseraPlus}
          />
          {hasCourseraPlus && (
            <Tag variant="status" priority="primary" css={styles.plusBadge}>
              PLUS
            </Tag>
          )}
        </div>
        <span css={styles.fullName}>{fullName}</span>
      </div>
      <Typography2 component="h3" variant="subtitleMedium" css={styles.yourAccount}>
        {_t('Your Account')}
      </Typography2>
      {!!courseraPlusSubscriptionCycle && (
        <div css={styles.ActiveSubscriptionsBorder}>
          <ActiveSubscriptions
            title={_t('Your current subscription:')}
            subtitle={
              courseraPlusSubscriptionCycle === 'MONTHLY' ? _t('Coursera Plus Monthly') : _t('Coursera Plus Annual')
            }
          />
        </div>
      )}
      {[...mobileMenuItems, <MobileLogOutButton tabIndex={0} key="signOut" />]}
    </div>
  );
};

export default AccountMenuItemExpanded;
